import { extendTheme, type ThemeConfig } from '@chakra-ui/react'

const config: ThemeConfig = {
  initialColorMode: 'dark',
  useSystemColorMode: false,
}

const theme = extendTheme({
  config,
  styles: {
    global: () => ({
      'html, body': {
        color: '#ffffff',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        minHeight: '100vh',
        // Dark base — background image is rendered as a real React DOM element in App.tsx
        // so Chakra's color-mode body style doesn't interfere with it
        backgroundColor: '#060910',
      },
      '*': {
        boxSizing: 'border-box',
      },
      '::-webkit-scrollbar': { width: '3px' },
      '::-webkit-scrollbar-track': { background: 'transparent' },
      '::-webkit-scrollbar-thumb': {
        background: 'rgba(255,255,255,0.08)',
        borderRadius: '3px',
      },
    }),
  },
  colors: {
    primary: {
      background: 'transparent',
      surface: 'rgba(255,255,255,0.04)',
      surfaceVariant: 'rgba(255,255,255,0.07)',
    },
    glass: {
      subtle:   'rgba(255,255,255,0.03)',
      base:     'rgba(255,255,255,0.05)',
      elevated: 'rgba(255,255,255,0.08)',
      high:     'rgba(255,255,255,0.12)',
      border:   'rgba(255,255,255,0.09)',
      borderHi: 'rgba(255,255,255,0.15)',
    },
    accent: {
      primary:      '#e0e4ea',
      primaryHover: '#ffffff',
      secondary:    '#a0a8b4',
      glow:         'rgba(255,255,255,0.2)',
    },
    text: {
      primary:   '#f1f3f1',
      secondary: '#8a9299',
      muted:     '#4a5058',
    },
    interactive: {
      active:   '#e0e4ea',
      inactive: '#2e3340',
    },
    level: {
      L1: '#FBBF24', L2: '#FB923C', L3: '#F87171', L4: '#60A5FA', L5: '#4ADE80',
    },
  },
  fonts: {
    heading: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    body:    "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  fontSizes: {
    xs: '11px', sm: '13px', md: '15px', lg: '18px', xl: '22px', '2xl': '26px',
  },
  radii: {
    small: '8px', medium: '14px', large: '20px', xl: '24px', pill: '999px',
  },
  shadows: {
    glass:   '0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
    glassMd: '0 8px 36px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
    glassLg: '0 16px 56px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.12)',
    soft: '0 4px 24px rgba(0,0,0,0.4)',
    medium: '0 8px 36px rgba(0,0,0,0.5)',
    strong: '0 16px 56px rgba(0,0,0,0.6)',
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: '700', borderRadius: 'medium',
        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)', minH: '44px',
      },
      variants: {
        primary: {
          bg: 'accent.primary', color: 'white',
          boxShadow: '0 4px 16px rgba(255,255,255,0.15)',
          _hover: { bg: 'accent.primaryHover', transform: 'translateY(-1px)', boxShadow: '0 6px 24px rgba(255,255,255,0.2)' },
          _active: { transform: 'translateY(0)' },
          _disabled: { opacity: 0.4, cursor: 'not-allowed' },
        },
        secondary: {
          bg: 'rgba(255,255,255,0.06)', color: 'accent.primary',
          border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(12px)',
          _hover: { bg: 'rgba(255,255,255,0.08)', transform: 'translateY(-1px)' },
        },
        ghost: {
          bg: 'transparent', color: 'text.secondary',
          _hover: { bg: 'rgba(255,255,255,0.06)', color: 'text.primary' },
        },
      },
      defaultProps: { variant: 'primary' },
    },
    Input: {
      variants: {
        outline: {
          field: {
            bg: 'rgba(255,255,255,0.05)',
            border: '1.5px solid rgba(255,255,255,0.09)',
            borderRadius: '14px',
            color: 'text.primary',
            backdropFilter: 'blur(12px)',
            _placeholder: { color: 'rgba(255,255,255,0.28)', fontSize: 'sm' },
            _hover: { bg: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.14)' },
            _focus: {
              bg: 'rgba(255,255,255,0.07)',
              borderColor: 'rgba(255,255,255,0.3)',
              boxShadow: '0 0 0 3px rgba(255,255,255,0.06)',
            },
          },
        },
      },
    },
    Textarea: {
      variants: {
        outline: {
          bg: 'rgba(255,255,255,0.05)',
          border: '1.5px solid rgba(255,255,255,0.09)',
          borderRadius: '14px',
          color: 'text.primary',
          backdropFilter: 'blur(12px)',
          _placeholder: { color: 'rgba(255,255,255,0.28)', fontSize: 'sm' },
          _hover: { bg: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.14)' },
          _focus: {
            bg: 'rgba(255,255,255,0.07)',
            borderColor: 'rgba(255,255,255,0.3)',
            boxShadow: '0 0 0 3px rgba(255,255,255,0.06)',
          },
        },
      },
    },
    Heading: {
      baseStyle: { color: 'text.primary', fontWeight: '700', letterSpacing: '-0.02em' },
    },
    Text: {
      baseStyle: { color: 'text.primary', lineHeight: '1.55' },
      variants: { secondary: { color: 'text.secondary' }, muted: { color: 'text.muted' } },
    },
  },
})

export default theme
